import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ArrowLeft, Calendar, TrendingUp, TrendingDown, AlertTriangle, Clock, DollarSign, BarChart3 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { API } from "../config/api";

export default function PaydayAnomalyPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartTimeframe, setChartTimeframe] = useState("1mo");
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPaydayData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/market/payday-indicator`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching payday data:", err);
      setError("Failed to load payday indicator data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChartData = useCallback(async () => {
    try {
      setChartLoading(true);
      const response = await axios.get(`${API}/market/indicators-history?timeframe=${chartTimeframe}`);
      if (response.data && response.data.chart_data) {
        setChartData(response.data.chart_data);
      }
    } catch (err) {
      console.error("Error fetching chart data:", err);
    } finally {
      setChartLoading(false);
    }
  }, [chartTimeframe]);

  useEffect(() => {
    fetchPaydayData();
    const interval = setInterval(fetchPaydayData, 300000);
    return () => clearInterval(interval);
  }, [fetchPaydayData]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const getSignalIcon = (signal) => {
    switch (signal) {
      case "BULLISH":
        return <TrendingUp className="w-6 h-6 text-green-400" />;
      case "CAUTION":
        return <AlertTriangle className="w-6 h-6 text-orange-400" />;
      default:
        return <TrendingDown className="w-6 h-6 text-gray-400" />;
    }
  };

  const getScoreGradient = (score) => {
    if (score >= 30) return "from-green-600 to-green-800";
    if (score >= 0) return "from-yellow-600 to-yellow-800";
    if (score >= -30) return "from-orange-600 to-orange-800";
    return "from-red-600 to-red-800";
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl">
          <p className="text-gray-400 text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value?.toFixed(2)}
              {entry.dataKey === 'market' ? '%' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading payday data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center text-red-400">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
          <p>{error}</p>
          <button
            onClick={fetchPaydayData}
            className="mt-4 px-4 py-2 bg-amber-600 rounded hover:bg-amber-500 text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => window.open("/", "_blank")}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Market Overview
        </button>

        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-amber-900/30 rounded-xl">
            <DollarSign className="w-8 h-8 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-amber-400">Payday Anomaly Indicator</h1>
            <p className="text-gray-400 text-sm">Market timing based on payroll cycles & holidays</p>
          </div>
        </div>

        {/* Main Score Card */}
        <div className={`bg-gradient-to-br ${getScoreGradient(data.score)} rounded-2xl p-6 md:p-8 mb-8 shadow-xl`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="text-6xl md:text-8xl font-bold mb-2">
                {data.score > 0 ? "+" : ""}{data.score}
              </div>
              <div className="text-xl md:text-2xl font-semibold opacity-90">{data.phase}</div>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2">
              <div className="flex items-center gap-2 text-2xl font-bold">
                {getSignalIcon(data.signal)}
                <span>{data.signal}</span>
              </div>
              <div className="text-sm opacity-80">
                {data.nearest_payday?.days_away === 0 
                  ? "PAYDAY TODAY" 
                  : `${data.nearest_payday?.days_away} days to nearest payday`}
              </div>
            </div>
          </div>
          <div className="mt-6 p-4 bg-black/20 rounded-xl">
            <p className="text-lg">{data.description}</p>
          </div>
        </div>

        {/* Payday Risk Chart */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-8" data-testid="payday-risk-chart">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-amber-400 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Market Indicators vs Payday Risk
            </h2>
            <div className="flex gap-2">
              {["1d", "5d", "1mo", "3mo"].map((tf) => (
                <button
                  key={tf}
                  onClick={() => setChartTimeframe(tf)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    chartTimeframe === tf
                      ? "bg-amber-600 text-white"
                      : "bg-slate-800 text-gray-400 hover:bg-slate-700"
                  }`}
                >
                  {tf.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          
          {/* Chart Legend */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <span className="text-gray-400">Payday Risk (0-100)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-gray-400">Market Price (%)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500"></div>
              <span className="text-gray-400">RSI (0-100)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-gray-400">MFI (0-100)</span>
            </div>
          </div>

          {chartLoading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400"></div>
            </div>
          ) : chartData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="time" 
                    stroke="#64748b" 
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  <YAxis 
                    yAxisId="left"
                    stroke="#64748b" 
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    domain={[0, 100]}
                    label={{ value: 'Index (0-100)', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }}
                  />
                  <YAxis 
                    yAxisId="right"
                    orientation="right"
                    stroke="#64748b" 
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    label={{ value: 'Market %', angle: 90, position: 'insideRight', fill: '#94a3b8', fontSize: 11 }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <ReferenceLine yAxisId="left" y={50} stroke="#6b7280" strokeDasharray="5 5" />
                  <ReferenceLine yAxisId="left" y={70} stroke="#ef4444" strokeDasharray="3 3" opacity={0.5} />
                  <ReferenceLine yAxisId="left" y={30} stroke="#22c55e" strokeDasharray="3 3" opacity={0.5} />
                  
                  {/* Payday Risk - Yellow */}
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="payday_risk"
                    stroke="#facc15"
                    strokeWidth={3}
                    dot={false}
                    name="Payday Risk"
                  />
                  
                  {/* Market Price - Purple */}
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="market"
                    stroke="#a855f7"
                    strokeWidth={2}
                    dot={false}
                    name="Market Price"
                  />
                  
                  {/* RSI - Red */}
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="rsi"
                    stroke="#ef4444"
                    strokeWidth={2}
                    dot={false}
                    name="RSI"
                  />
                  
                  {/* MFI - Green */}
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="mfi"
                    stroke="#22c55e"
                    strokeWidth={2}
                    dot={false}
                    name="MFI"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-80 flex items-center justify-center text-gray-500">
              No chart data available
            </div>
          )}
          
          <div className="mt-4 text-xs text-gray-500 text-center">
            Payday Risk 50 = Neutral | Below 30 = Risk (pre-payday) | Above 70 = Opportunity (post-payday)
          </div>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          {/* Upcoming Paydays */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="upcoming-paydays">
            <h2 className="text-lg font-semibold text-amber-400 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Upcoming Paydays
            </h2>
            <div className="space-y-3">
              {data.upcoming_paydays?.map((pd, i) => (
                <div 
                  key={i} 
                  className={`flex items-center justify-between p-4 rounded-lg ${
                    pd.days_away === 0 ? "bg-amber-900/30 border border-amber-700" : "bg-slate-800"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${
                      pd.days_away === 0 ? "bg-amber-400 animate-pulse" : 
                      pd.days_away <= 2 ? "bg-orange-400" : "bg-gray-500"
                    }`}></div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {pd.type}
                        {pd.holiday_adjusted && (
                          <span className="text-xs bg-amber-600/30 text-amber-400 px-2 py-0.5 rounded">
                            Holiday Adjusted
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-400">
                        {pd.date}
                        {pd.original_date && pd.original_date !== pd.date && (
                          <span className="ml-2 text-amber-500/70">(was {pd.original_date})</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-lg font-bold ${
                      pd.days_away === 0 ? "text-amber-400" : "text-white"
                    }`}>
                      {pd.days_away === 0 ? "TODAY" : `${pd.days_away}d`}
                    </div>
                    <div className="text-sm text-gray-500">{pd.coverage} coverage</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Holiday Information */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="holiday-info">
            <h2 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Holiday Information
            </h2>
            
            {/* Market Status */}
            <div className={`p-4 rounded-lg mb-4 ${
              data.holiday_info?.is_market_closed 
                ? "bg-red-900/30 border border-red-700" 
                : "bg-green-900/30 border border-green-700"
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${
                  data.holiday_info?.is_market_closed ? "bg-red-400" : "bg-green-400 animate-pulse"
                }`}></div>
                <div>
                  <div className="font-medium">
                    {data.holiday_info?.is_market_closed ? "Market Closed" : "Market Open"}
                  </div>
                  {data.holiday_info?.market_closed_reason && (
                    <div className="text-sm text-gray-400">{data.holiday_info.market_closed_reason}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Today's Holiday Status */}
            {data.holiday_info?.is_today_holiday && (
              <div className="p-4 bg-red-900/20 border border-red-800 rounded-lg mb-4">
                <div className="text-red-400 font-medium">🎉 Today is a Holiday</div>
                <div className="text-white">{data.holiday_info.today_holiday_name}</div>
              </div>
            )}

            {/* Upcoming Holidays */}
            <div className="space-y-3">
              <div className="text-sm text-gray-400 uppercase tracking-wide">Upcoming Market Holidays</div>
              {data.holiday_info?.upcoming_holidays?.length > 0 ? (
                data.holiday_info.upcoming_holidays.map((h, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="text-2xl">🗓️</div>
                      <div>
                        <div className="font-medium text-white">{h.name}</div>
                        <div className="text-sm text-gray-400">{h.date}</div>
                      </div>
                    </div>
                    <div className={`text-lg font-bold ${
                      h.days_away <= 3 ? "text-red-400" : "text-gray-400"
                    }`}>
                      {h.days_away === 0 ? "Today" : `${h.days_away}d`}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 italic p-3">No holidays in the next 30 days</div>
              )}
            </div>
          </div>
        </div>

        {/* Interpretation Guide */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-8" data-testid="interpretation">
          <h2 className="text-lg font-semibold text-blue-400 mb-4">📊 How to Interpret</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 bg-red-900/20 border border-red-800/50 rounded-lg">
              <div className="text-red-400 font-semibold mb-2">Pre-Payday Risk</div>
              <p className="text-sm text-gray-300">{data.interpretation?.pre_payday_risk}</p>
            </div>
            <div className="p-4 bg-yellow-900/20 border border-yellow-800/50 rounded-lg">
              <div className="text-yellow-400 font-semibold mb-2">Payday Neutral</div>
              <p className="text-sm text-gray-300">{data.interpretation?.payday_neutral}</p>
            </div>
            <div className="p-4 bg-green-900/20 border border-green-800/50 rounded-lg">
              <div className="text-green-400 font-semibold mb-2">Post-Payday Bullish</div>
              <p className="text-sm text-gray-300">{data.interpretation?.post_payday_bullish}</p>
            </div>
            <div className="p-4 bg-purple-900/20 border border-purple-800/50 rounded-lg">
              <div className="text-purple-400 font-semibold mb-2">Holiday Impact</div>
              <p className="text-sm text-gray-300">{data.interpretation?.holiday_impact}</p>
            </div>
          </div>
        </div>

        {/* Score Legend */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="score-legend">
          <h2 className="text-lg font-semibold text-gray-400 mb-4">Score Legend</h2>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-green-600 to-green-800"></div>
              <span className="text-sm text-gray-300">+30 to +100: Bullish (post-payday buying)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-yellow-600 to-yellow-800"></div>
              <span className="text-sm text-gray-300">0 to +29: Neutral</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-orange-600 to-orange-800"></div>
              <span className="text-sm text-gray-300">-1 to -30: Caution</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-gradient-to-r from-red-600 to-red-800"></div>
              <span className="text-sm text-gray-300">-31 to -100: Risk (pre-payday selling)</span>
            </div>
          </div>
        </div>

        {/* Timestamp */}
        <div className="text-center text-gray-500 text-sm mt-8">
          Last updated: {data.timestamp ? new Date(data.timestamp).toLocaleString() : "N/A"}
        </div>
      </div>
    </div>
  );
}
